import Model, { attr } from '@ember-data/model';
import { getOwner } from '@ember/application';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { validator, buildValidations } from '@eflexsystems/ember-tracked-validations';
import { intersection } from 'ramda';
import { userRoles } from 'cpc-license-manager/constants/user-roles';
import { arrayAttr } from 'cpc-license-manager/decorators';
import { checkUniqueField } from 'cpc-license-manager/utils/validators';

const inRole = (user, requiredRole) => {
  const role = userRoles[user.role];

  if (role == null) {
    return false;
  }

  return role.level >= requiredRole.level;
};

@buildValidations({
  timeoutLength: [
    validator('number', {
      gte: 2,
      lte: 9_999_999,
      allowString: true,
      allowNone: false,
      get disabled() {
        return this.model.isSso;
      },
    }),
    validator('presence', {
      presence: true,
      get disabled() {
        return this.model.isSso;
      },
    }),
  ],
  userName: [
    validator('presence', true),
    validator('length', { min: 4 }),
    validator('inline', {
      validate(userName) {
        if (userName?.includes(' ')) {
          return 'Username cannot contain spaces';
        }
        return true;
      },
    }),
    validator('inline', {
      validate(userName, options, user, attribute) {
        const checked = getOwner(user).lookup('service:userRepo').users.filter(item => !item.isDeleted);
        return checkUniqueField(user, checked, attribute).result;
      },
    }),
  ],

  password: [
    validator('presence', {
      presence: true,
      get disabled() {
        return !this.model.isNew || this.model.isSso;
      },
    }),
  ],

  passwordConfirmation: [
    validator('confirmation', {
      get disabled() {
        return !this.model.isNew;
      },
      on: 'password',
    }),
  ],

  role: [validator('presence', true)],
})
class User extends Model {
  @attr('date', { defaultValue() { return new Date(); } }) createdAt;
  @attr('string') userName;
  @attr('string', { defaultValue: '' }) firstName;
  @attr('string', { defaultValue: '' }) lastName;
  @attr('string') email;
  @attr('string') notes;
  @attr('string') role;
  @attr('string') password;
  @attr('string') encryptedPassword;
  @attr('boolean', { defaultValue: true }) isEnabled;
  @attr('number', { defaultValue: 1440 }) timeoutLength;
  @attr('boolean', { defaultValue: true }) timeoutWarning;
  @attr('boolean', { defaultValue: false }) isSso;

  @arrayAttr permissions;
  @arrayAttr authorizationTags;

  @tracked passwordConfirmation;

  get errorIdentifier() {
    return this.userName;
  }

  get isAdmin() {
    return inRole(this, userRoles.Admin);
  }

  get isNotAdmin() {
    return !this.isAdmin;
  }

  get isUser() {
    return inRole(this, userRoles.User);
  }

  get isNotUser() {
    return !this.isUser;
  }

  get isViewOnly() {
    return inRole(this, userRoles.ViewOnly);
  }

  get isNotViewOnly() {
    return !this.isViewOnly;
  }

  get displayUsername() {
    const fullName = `${this.firstName} ${this.lastName}`;

    if (fullName.trim().length > 0) {
      return fullName;
    } else {
      return this.userName;
    }
  }

  hasPermission(permission, includeAdmin = true) {
    return (includeAdmin && this.isAdmin) || this.permissions?.includes(permission);
  }

  hasPermissions(permissions) {
    return this.isAdmin || permissions.every((val) => this.hasPermission(val));
  }

  hasAnyPermission(permissions) {
    return this.isAdmin || permissions.some((val) => this.hasPermission(val));
  }

  hasAuthorization(authorizedTags) {
    return this.isAdmin || !isEmpty(intersection(this.authorizationTags, authorizedTags));
  }

  toFragment() {
    return {
      id: this.id,
      firstName: this.firstName,
      lastName: this.lastName,
      userName: this.userName,
    };
  }
}

export default User;
